import React from 'react';
import { FaBriefcase, FaGraduationCap, FaAward, FaLaptopCode } from 'react-icons/fa';

const Experience = () => {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=Space+Mono:wght@400;700&display=swap');

        :root {
          --accent:  #00f5d4;
          --accent2: #7b61ff;
          --accent3: #ff4ecd;
          --bg:      #07090f;
          --text:    #e8eaf6;
          --muted:   rgba(232,234,246,0.45);
          --border:  rgba(0,245,212,0.13);
          --card-bg: rgba(255,255,255,0.03);
        }

        .ex-section {
          position: relative;
          padding: 6rem 2rem;
          background: var(--bg);
          font-family: 'Syne', sans-serif;
          overflow: hidden;
        }

        .ex-section::before {
          content: '';
          position: absolute;
          inset: 0;
          z-index: 0;
          background:
            radial-gradient(ellipse 50% 60% at 80% 20%, rgba(123,97,255,0.1) 0%, transparent 70%),
            radial-gradient(ellipse 40% 50% at 10% 80%, rgba(0,245,212,0.07) 0%, transparent 70%);
          pointer-events: none;
        }

        .ex-section::after {
          content: '';
          position: absolute;
          inset: 0;
          z-index: 0;
          background: repeating-linear-gradient(
            0deg,
            transparent, transparent 2px,
            rgba(0,245,212,0.015) 2px, rgba(0,245,212,0.015) 4px
          );
          pointer-events: none;
        }

        .ex-inner {
          position: relative;
          z-index: 1;
          max-width: 1280px;
          margin: 0 auto;
        }

        .ex-heading {
          text-align: center;
          margin-bottom: 4rem;
          opacity: 0;
          animation: exFadeUp 0.6s ease 0.1s forwards;
        }

        .ex-tag {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-family: 'Space Mono', monospace;
          font-size: 0.68rem;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--accent);
          margin-bottom: 1rem;
        }

        .ex-tag::before, .ex-tag::after {
          content: '';
          display: inline-block;
          width: 20px;
          height: 1.5px;
          background: var(--accent);
          box-shadow: 0 0 8px var(--accent);
        }

        .ex-main-title {
          font-size: clamp(1.8rem, 4vw, 2.8rem);
          font-weight: 800;
          letter-spacing: -0.03em;
          color: #fff;
        }

        .ex-main-title .ex-accent {
          background: linear-gradient(90deg, var(--accent) 0%, var(--accent2) 60%, var(--accent3) 100%);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          background-size: 200% auto;
          animation: exGrad 4s linear infinite;
        }

        .ex-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2.5rem;
        }

        /* Column Headers */
        .ex-col-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 2rem;
          padding-bottom: 0.75rem;
          border-bottom: 2px solid var(--border);
        }

        .ex-col-header svg {
          font-size: 1.8rem;
          color: var(--accent);
        }

        .ex-col-header h3 {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0;
          background: linear-gradient(135deg, #fff, var(--accent));
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }

        /* Cards */
        .ex-card {
          background: var(--card-bg);
          border: 1px solid var(--border);
          border-radius: 20px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          transition: all 0.35s ease;
          backdrop-filter: blur(2px);
          opacity: 0;
          transform: translateY(20px);
          animation: exCardReveal 0.5s ease forwards;
        }

        .ex-card:last-child {
          margin-bottom: 0;
        }

        .ex-card:nth-child(1) { animation-delay: 0.05s; }
        .ex-card:nth-child(2) { animation-delay: 0.1s; }
        .ex-card:nth-child(3) { animation-delay: 0.15s; }
        .ex-card:nth-child(4) { animation-delay: 0.2s; }

        .ex-card:hover {
          border-color: rgba(0,245,212,0.35);
          background: rgba(0,245,212,0.04);
          transform: translateY(-4px);
          box-shadow: 0 15px 30px -12px rgba(0,0,0,0.5);
        }

        .ex-card-title {
          font-size: 1.2rem;
          font-weight: 700;
          color: #fff;
          margin-bottom: 0.5rem;
          letter-spacing: -0.01em;
        }

        .ex-card-subtitle {
          font-family: 'Space Mono', monospace;
          font-size: 0.7rem;
          color: var(--accent);
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .ex-card-subtitle svg {
          font-size: 0.75rem;
        }

        .ex-card-list {
          list-style: none;
          padding: 0;
          margin: 1rem 0 0 0;
        }

        .ex-card-list li {
          font-size: 0.85rem;
          color: var(--muted);
          margin-bottom: 0.5rem;
          padding-left: 1.2rem;
          position: relative;
          line-height: 1.5;
        }

        .ex-card-list li::before {
          content: '▹';
          position: absolute;
          left: 0;
          color: var(--accent2);
          font-size: 0.7rem;
        }

        .ex-badge-group {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-top: 1rem;
        }

        .ex-badge {
          background: rgba(0,245,212,0.1);
          border: 1px solid rgba(0,245,212,0.2);
          border-radius: 40px;
          padding: 0.25rem 0.8rem;
          font-family: 'Space Mono', monospace;
          font-size: 0.6rem;
          font-weight: 500;
          color: var(--accent);
          letter-spacing: 0.02em;
        }

        .ex-badge-success {
          background: rgba(0,245,212,0.15);
          border-color: rgba(0,245,212,0.3);
        }

        .ex-badge-info {
          background: rgba(123,97,255,0.15);
          border-color: rgba(123,97,255,0.3);
          color: var(--accent2);
        }

        .ex-badge-warning {
          background: rgba(255,78,205,0.15);
          border-color: rgba(255,78,205,0.3);
          color: var(--accent3);
        }

        /* Highlights Section */
        .ex-highlights {
          margin-top: 4rem;
          padding-top: 2rem;
          border-top: 1px solid var(--border);
        }

        .ex-highlights-title {
          text-align: center;
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 2rem;
          background: linear-gradient(135deg, #fff, var(--accent2));
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }

        .ex-stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1.5rem;
        }

        .ex-stat-card {
          background: var(--card-bg);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 1.5rem;
          text-align: center;
          transition: all 0.3s ease;
        }

        .ex-stat-card:hover {
          border-color: var(--accent);
          transform: translateY(-5px);
          background: rgba(0,245,212,0.04);
        }

        .ex-stat-number {
          font-size: 2.2rem;
          font-weight: 800;
          background: linear-gradient(135deg, var(--accent), var(--accent2));
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          margin-bottom: 0.5rem;
        }

        .ex-stat-label {
          font-family: 'Space Mono', monospace;
          font-size: 0.7rem;
          color: var(--muted);
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }

        @keyframes exFadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        @keyframes exGrad {
          0%   { background-position: 0% center; }
          100% { background-position: 200% center; }
        }

        @keyframes exCardReveal {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        @media (max-width: 900px) {
          .ex-section { padding: 4rem 1.5rem; }
          .ex-grid { grid-template-columns: 1fr; gap: 2rem; }
          .ex-stats-grid { grid-template-columns: repeat(2, 1fr); }
        }

        @media (max-width: 480px) {
          .ex-stats-grid { grid-template-columns: 1fr; }
          .ex-card-title { font-size: 1rem; }
        }
      `}</style>

      <section id="experience" className="ex-section">
        <div className="ex-inner">
          <div className="ex-heading">
            <div className="ex-tag">My Journey</div>
            <h2 className="ex-main-title">
              Experience & <span className="ex-accent">Education</span>
            </h2>
          </div>

          <div className="ex-grid">
            {/* Left Column - Work Experience */}
            <div>
              <div className="ex-col-header">
                <FaBriefcase />
                <h3>Work Experience</h3>
              </div>

              <div className="ex-card">
                <h4 className="ex-card-title">Administrative Executive - HR & Operations</h4>
                <div className="ex-card-subtitle">
                  <FaBriefcase /> Essbee Fire Service (Remote) | Sep 2024 - Aug 2025
                </div>
                <ul className="ex-card-list">
                  <li>Successfully worked remotely for 12 months managing all operations</li>
                  <li>Coordinated with teams across different locations using digital tools</li>
                  <li>Managed documentation, data entry, and reporting independently</li>
                  <li>Met all deadlines consistently without physical supervision</li>
                  <li>Demonstrated exceptional self-discipline and time management</li>
                </ul>
                <div className="ex-badge-group">
                  <span className="ex-badge ex-badge-success">🏠 Remote Work</span>
                  <span className="ex-badge ex-badge-info">⏱️ Time Management</span>
                  <span className="ex-badge ex-badge-warning">💪 Self-Disciplined</span>
                </div>
              </div>

              <div className="ex-card">
                <h4 className="ex-card-title">Self-Taught Developer Journey</h4>
                <div className="ex-card-subtitle">
                  <FaLaptopCode /> 2024 - Present
                </div>
                <ul className="ex-card-list">
                  <li>Self-taught MERN Stack development during evening hours</li>
                  <li>Built multiple full-stack applications showcasing practical skills</li>
                  <li>Completed online courses and hands-on projects</li>
                  <li>Leveraged AI tools for efficient learning and problem-solving</li>
                  <li>Created portfolio of working applications</li>
                </ul>
                <div className="ex-badge-group">
                  <span className="ex-badge ex-badge-success">⚛️ MERN Stack</span>
                  <span className="ex-badge ex-badge-info">📚 Self-Learning</span>
                  <span className="ex-badge ex-badge-warning">🤖 AI Tools</span>
                </div>
              </div>
            </div>

            {/* Right Column - Education */}
            <div>
              <div className="ex-col-header">
                <FaGraduationCap />
                <h3>Education</h3>
              </div>

              <div className="ex-card">
                <h4 className="ex-card-title">Bachelor of Technology (B.Tech)</h4>
                <div className="ex-card-subtitle">
                  Information Technology | 2017
                </div>
                <ul className="ex-card-list">
                  <li>Asansol Engineering College</li>
                  <li>MAKAUT (WBUT)</li>
                  <li><strong>Percentage:</strong> 63.70%</li>
                </ul>
              </div>

              <div className="ex-card">
                <h4 className="ex-card-title">Higher Secondary (Science Stream)</h4>
                <div className="ex-card-subtitle">2012</div>
                <ul className="ex-card-list">
                  <li>S.H.M.S. Inter Mahavidyalaya, Kumardhubi</li>
                  <li>JAC Board</li>
                </ul>
              </div>

              <div className="ex-card">
                <h4 className="ex-card-title">Madhyamik (Secondary)</h4>
                <div className="ex-card-subtitle">2010</div>
                <ul className="ex-card-list">
                  <li>Sodepur Colliery High School</li>
                  <li>WBBSE Board</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Key Highlights Stats */}
          <div className="ex-highlights">
            <h3 className="ex-highlights-title">
              <FaAward style={{ marginRight: '0.5rem', color: 'var(--accent)' }} />
              Key Highlights
            </h3>
            <div className="ex-stats-grid">
              <div className="ex-stat-card">
                <div className="ex-stat-number">1 Year</div>
                <div className="ex-stat-label">Remote Work Experience</div>
              </div>
              <div className="ex-stat-card">
                <div className="ex-stat-number">6+</div>
                <div className="ex-stat-label">Projects Built</div>
              </div>
              <div className="ex-stat-card">
                <div className="ex-stat-number">100%</div>
                <div className="ex-stat-label">Self-Taught</div>
              </div>
              <div className="ex-stat-card">
                <div className="ex-stat-number">MERN</div>
                <div className="ex-stat-label">Stack Expert</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Experience;